import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Toaster, toast } from "sonner";
import Staff from "@/components/Staff";
import StudioAuthNav from "@/components/StudioAuthNav";
import LabStamp from "@/components/LabStamp";
import { unlockAudio } from "@/lib/synth";
import { playSequenceAudio, playEventsExact } from "@/lib/playEngine";
import { voiceForPreset, PRESETS } from "@/lib/presets";
import { buildStaffAndAudio, isHebrewComposition } from "@/lib/compositionRender";
import { measureSequence } from "@/lib/measures";
import Harp, { letterIndexOf } from "@/components/Harp";
import { alefNoteForMode, ROOTS, SCALES, DEFAULT_SCALE } from "@/lib/hebrewCryptogram";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(d) {
  try {
    const dt = new Date(d);
    return dt.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (e) {
    return "";
  }
}

/** A harp piece in the library: strings light up as the piece plays. */
function LibraryHarp({ comp, entries, activeIdx }) {
  const harpRef = useRef(null);
  const [lit, setLit] = useState(() => new Set());

  useEffect(() => {
    if (activeIdx < 0) {
      setLit(new Set());
      return;
    }
    const letterIdx = letterIndexOf(entries[activeIdx]);
    if (letterIdx < 0) return;
    setLit((prev) => new Set(prev).add(letterIdx));
    harpRef.current?.pluck(letterIdx);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIdx]);

  return (
    <Harp theme="light"
      ref={harpRef}
      scaleOptions={{
        root: comp.scale_root || "G",
        modeIdx: comp.scale_mode ?? 0,
        scale: comp.scale_family || DEFAULT_SCALE,
      }}
      lit={lit}
      disabled
    />
  );
}


export default function Library() {
  const [items, setItems] = useState([]);
  const [activeNoteIdx, setActiveNoteIdx] = useState(-1);
  const [loading, setLoading] = useState(true);
  const [playingId, setPlayingId] = useState(null);
  const [query, setQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");

  // Only offer filters for cycles that are actually represented, plus "typed"
  // for pieces someone wrote by hand and "remix" for tapped versions.
  const filters = useMemo(() => {
    const out = [{ value: "all", label: "All" }];
    const seen = new Map();
    items.forEach((c) => {
      const cycle = (c.source_label || "").split(" · ")[0].trim();
      if (cycle) seen.set(cycle.toLowerCase(), cycle);
    });
    [...seen.entries()].forEach(([value, label]) => out.push({ value, label }));
    if (items.some((c) => !c.source_label)) out.push({ value: "typed", label: "Typed" });
    if (items.some((c) => (c.remix_events || []).length || (c.remix_taps || []).length)) {
      out.push({ value: "remix", label: "Remixes" });
    }
    return out;
  }, [items]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((c) => {
      const cycle = (c.source_label || "").split(" · ")[0].trim().toLowerCase();
      const isRemix = !!((c.remix_events || []).length || (c.remix_taps || []).length);
      if (activeFilter === "typed" && c.source_label) return false;
      if (activeFilter === "remix" && !isRemix) return false;
      if (!["all", "typed", "remix"].includes(activeFilter) && cycle !== activeFilter) return false;
      if (!q) return true;
      const haystack = [c.title, (c.words || []).join(" "), c.source_label, c.source_ref]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [items, query, activeFilter]);

  // Hebrew only — the English studio is gone, so any Latin-letter leftovers
  // are filtered out of the list and purged from the server.
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/compositions`, { withCredentials: true });
      setItems((res.data || []).filter(isHebrewComposition));
    } catch (e) {
      toast.error("Couldn't load your library");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/compositions/${id}`, { withCredentials: true });
      toast.success("Deleted");
      setItems((prev) => prev.filter((c) => c.id !== id));
    } catch (e) {
      toast.error("Couldn't delete");
    }
  };

  // Deterministic stepMs estimate for older saves (no remix_events) lives in
  // /app/frontend/src/lib/compositionRender.js — see buildStaffAndAudio.

  const handleShare = async (c) => {
    const url = `${window.location.origin}/composition/${c.id}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Share link copied");
    } catch {
      // Fallback: show the URL in a toast for manual copy.
      toast.message("Share link", { description: url, duration: 8000 });
    }
  };

  const openInStudio = (c) => {
    const wordsParam = encodeURIComponent(c.words.join(" "));
    const root = c.scale_root ? `&root=${encodeURIComponent(c.scale_root)}` : "";
    const mode = Number.isFinite(c.scale_mode) ? `&mode=${c.scale_mode}` : "";
    const scale = c.scale_family ? `&scale=${encodeURIComponent(c.scale_family)}` : "";
    const measured = c.measured ? "&measured=1" : "";
    const condensed = c.condensed ? "&condensed=1" : "";
    const page = c.instrument === "harp" ? "harp" : "hebrew";
    return `/app/${page}?words=${wordsParam}${root}${mode}${scale}${measured}${condensed}`;
  };

  const handlePlay = async (comp) => {
    if (playingId) return;
    unlockAudio();
    setPlayingId(comp.id);
    setActiveNoteIdx(-1);
    const { audioEvents, mode } = buildStaffAndAudio(comp);
    const isHarp = comp.instrument === "harp";
    if (mode === "events") {
      let i = 0;
      await playEventsExact(audioEvents, {
        voice: isHarp ? "harp" : voiceForPreset(comp.preset_key || "meditation"),
        onNote: () => {
          const idx = i;
          setActiveNoteIdx(idx);
          i += 1;
        },
      });
    } else if (isHarp) {
      await playSequenceAudio(audioEvents, {
        presetKey: comp.preset_key || "meditation",
        voice: "harp",
        pedal: false,
        onTick: (idx) => setActiveNoteIdx(idx),
      });
    } else if (comp.measured) {
      let noteIdx = -1;
      await playSequenceAudio(measureSequence(audioEvents).steps, {
        presetKey: comp.preset_key || "meditation",
        measured: true,
        pedal: comp.pedal !== undefined ? comp.pedal : true,
        onTick: (_idx, step) => {
          if (step.rest) return;
          noteIdx += 1;
          setActiveNoteIdx(noteIdx);
        },
      });
    } else {
      await playSequenceAudio(audioEvents, {
        presetKey: comp.preset_key || "meditation",
        pedal: comp.pedal !== undefined ? comp.pedal : true,
        onTick: (idx) => setActiveNoteIdx(idx),
      });
    }
    setActiveNoteIdx(-1);
    setPlayingId(null);
  };

  return (
    <div
      className="min-h-screen w-full"
      style={{
        backgroundColor: "#FAF8F5",
        color: "#1A1523",
        fontFamily: "'Courier Prime', monospace",
      }}
    >
      <Toaster position="bottom-right" richColors />

      <header className="max-w-[1100px] mx-auto px-6 pt-10 pb-6 flex items-end justify-between gap-6">
        <div>
          <LabStamp testId="library-lab-stamp" />
          <h1
            className="mt-3 text-4xl sm:text-5xl lg:text-6xl leading-[0.95]"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}
            data-testid="library-title"
          >
            Library<span style={{ color: "#E86F4A" }}>.</span>
          </h1>
          <p className="mt-3 text-sm" style={{ color: "#5C5268" }}>
            Every saved score, kept in one place.
          </p>
        </div>
        <nav className="text-sm flex gap-4 items-center">
          <StudioAuthNav />
          <Link
            to="/app/hebrew"
            data-testid="nav-hebrew-link"
            className="underline underline-offset-4 hover:text-[#E86F4A] transition-colors"
          >
            &lsaquo; The Alephonics App
          </Link>
        </nav>
      </header>

      <main className="max-w-[1100px] mx-auto px-6 pb-20">
        {/* Search + filters. Sources come from whatever's actually saved. */}
        {items.length > 0 && (
          <div className="mb-8 flex flex-wrap items-center gap-3" data-testid="library-controls">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search words, title or source"
              data-testid="library-search"
              className="h-10 px-3 rounded-md text-sm outline-none flex-1 min-w-[15rem]"
              style={{
                border: "1px solid #EBE3D5",
                backgroundColor: "#FFFFFF",
                color: "#1A1523",
                fontFamily: "'Courier Prime', monospace",
              }}
            />
            <div className="flex flex-wrap gap-2">
              {filters.map((f) => {
                const active = activeFilter === f.value;
                return (
                  <button
                    key={f.value}
                    type="button"
                    onClick={() => setActiveFilter(f.value)}
                    data-testid={`library-filter-${f.value}`}
                    className="h-10 px-3 rounded-md text-xs uppercase transition-colors"
                    style={{
                      fontFamily: "'Courier Prime', monospace",
                      letterSpacing: "0.12em",
                      border: `1px solid ${active ? "#D99B26" : "#EBE3D5"}`,
                      backgroundColor: active ? "rgba(217,155,38,0.16)" : "#FFFFFF",
                      color: active ? "#8A6514" : "#5C5268",
                    }}
                  >
                    {f.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {loading ? (
          <p className="text-sm" style={{ color: "#5C5268" }}>
            Loading compositions…
          </p>
        ) : items.length > 0 && visible.length === 0 ? (
          <div
            className="rounded-md border p-10 text-center"
            style={{ borderColor: "#EBE3D5", backgroundColor: "#FFFBF2" }}
            data-testid="library-no-matches"
          >
            <p className="text-2xl mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Nothing matches that.
            </p>
            <button
              type="button"
              onClick={() => {
                setQuery("");
                setActiveFilter("all");
              }}
              className="text-sm underline underline-offset-4"
              style={{ color: "#E86F4A" }}
              data-testid="library-clear-filters"
            >
              Clear the search
            </button>
          </div>
        ) : items.length === 0 ? (
          <div
            className="rounded-md border p-10 text-center"
            style={{ borderColor: "#EBE3D5", backgroundColor: "#FFFBF2" }}
            data-testid="library-empty"
          >
            <p
              className="text-2xl mb-2"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Nothing here yet.
            </p>
            <p className="text-sm" style={{ color: "#5C5268" }}>
              Head back to The Alephonics App and save your first composition.
            </p>
          </div>
        ) : (
          <ul className="space-y-8" data-testid="library-list">
            {visible.map((c) => {
              const { staffEntries: entries } = buildStaffAndAudio(c);
              const isRemix =
                !!(c.remix_taps && c.remix_taps.length > 0) ||
                !!(c.remix_events && c.remix_events.length > 0);
              const isHebrew = isHebrewComposition(c);
              const rootLabel = isHebrew
                ? (ROOTS.find((r) => r.value === (c.scale_root || "D"))?.label || c.scale_root || "D")
                : null;
              const scaleName = isHebrew
                ? (SCALES.find((s) => s.value === (c.scale_family || DEFAULT_SCALE))?.label || "")
                : null;
              const alefNote = isHebrew
                ? alefNoteForMode(c.scale_root || "D", c.scale_mode ?? 0, c.scale_family || DEFAULT_SCALE)
                : null;
              const isThisPlaying = playingId === c.id;
              return (
                <li
                  key={c.id}
                  data-testid={`library-item-${c.id}`}
                  className="rounded-md border p-5"
                  style={{ borderColor: "#EBE3D5", backgroundColor: "#FFFBF2" }}
                >
                  <div className="flex flex-wrap items-baseline justify-between gap-3 mb-3">
                    <div>
                      <h3
                        className="text-2xl"
                        style={{ fontFamily: "'Cormorant Garamond', serif" }}
                      >
                        {c.title}
                      </h3>
                      <p className="text-xs mt-1" style={{ color: "#5C5268" }}>
                        {c.words.join(" · ")} &nbsp;—&nbsp; {formatDate(c.created_at)}
                      </p>
                      <div className="flex gap-2 mt-2 text-[10px] uppercase tracking-[0.16em] flex-wrap">
                        {isHebrew && (
                          <span
                            data-testid={`library-scale-${c.id}`}
                            className="px-2 py-0.5 rounded-full border"
                            style={{
                              borderColor: "#FFFFFF",
                              backgroundColor: "transparent",
                              color: "#5C5268",
                              fontFamily: "'Courier Prime', monospace",
                            }}
                          >
                            {rootLabel} {scaleName} · aleph on {alefNote}
                          </span>
                        )}
                        <span
                          data-testid={`library-feel-${c.id}`}
                          className="px-2 py-0.5 rounded-full border"
                          style={{
                            borderColor: "#D99B26",
                            color: "#8A6514",
                            fontFamily: "'Courier Prime', monospace",
                          }}
                        >
                          {PRESETS[c.preset_key]?.label || "Meditative"}
                        </span>
                        {c.source_label && (
                          <span
                            data-testid={`library-source-${c.id}`}
                            className="px-2 py-0.5 rounded-full border"
                            style={{
                              borderColor: "#EBE3D5",
                              color: "#E86F4A",
                              fontFamily: "'Courier Prime', monospace",
                            }}
                            title={c.source_ref || ""}
                          >
                            {c.source_label}
                          </span>
                        )}
                        {isRemix && (
                          <span
                            className="px-2 py-0.5 rounded-full"
                            style={{
                              backgroundColor: "#FFFFFF",
                              color: "#8A6514",
                              fontFamily: "'Courier Prime', monospace",
                            }}
                          >
                            {c.instrument === "harp"
                              ? "Harp · my timing"
                              : `Remix${c.remix_taps && c.remix_taps.length ? ` · ${c.remix_taps.length} taps` : ""}`}
                          </span>
                        )}
                        {c.instrument === "harp" && !isRemix && (
                          <span
                            className="px-2 py-0.5 rounded-full"
                            style={{
                              backgroundColor: "#FFFFFF",
                              color: "#8A6514",
                              fontFamily: "'Courier Prime', monospace",
                            }}
                          >
                            Harp
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <Button
                        data-testid={`library-play-${c.id}`}
                        onClick={() => handlePlay(c)}
                        disabled={playingId !== null}
                        className="rounded-full text-white"
                        style={{
                          backgroundColor: "#1B6C7A",
                          fontFamily: "'Courier Prime', monospace",
                          letterSpacing: "0.16em",
                        }}
                      >
                        {playingId === c.id ? "Playing…" : "Play"}
                      </Button>
                      <Link
                        to={openInStudio(c)}
                        data-testid={`library-open-${c.id}`}
                        className="inline-flex items-center rounded-full border border-[#1A1523] text-[#1A1523] hover:bg-[#1A1523]/5 px-4 h-9 text-sm"
                        style={{ fontFamily: "'Courier Prime', monospace", letterSpacing: "0.16em" }}
                      >
                        Open in the app
                      </Link>
                      <Button
                        data-testid={`library-share-${c.id}`}
                        onClick={() => handleShare(c)}
                        variant="outline"
                        className="rounded-full border-[#1A1523] text-[#1A1523] hover:bg-[#1A1523]/5"
                        style={{ fontFamily: "'Courier Prime', monospace", letterSpacing: "0.16em" }}
                      >
                        Share
                      </Button>
                      <Button
                        data-testid={`library-delete-${c.id}`}
                        onClick={() => handleDelete(c.id)}
                        variant="outline"
                        className="rounded-full border-[#1A1523] text-[#1A1523] hover:bg-[#1A1523]/5"
                        style={{ fontFamily: "'Courier Prime', monospace", letterSpacing: "0.16em" }}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                  {c.instrument === "harp" ? (
                    <LibraryHarp
                      comp={c}
                      entries={entries}
                      activeIdx={isThisPlaying ? activeNoteIdx : -1}
                    />
                  ) : (
                    <Staff theme="light"
                      droppedEntries={entries}
                      activeIndex={isThisPlaying ? activeNoteIdx : -1}
                      measuredSteps={
                        c.measured && !isRemix ? measureSequence(entries).steps : null
                      }
                      baseStepMs={800}
                    />
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </main>
    </div>
  );
}
